import { useState } from 'react';
import { Card, SLabel } from '../components/Atoms';
import { C, TASK_PRIORITY } from '../constants';

const TASK_TYPES = [
  { k: 'general', label: 'General', color: '#C8DFF0' },
  { k: 'labs',    label: 'Labs',    color: '#FF9500'  },
  { k: 'imaging', label: 'Imaging', color: '#64D2FF'  },
];
const TASK_STATUS = {
  general: ['Pending', 'Done'],
  labs:    ['Pending', 'Done', 'With Results'],
  imaging: ['Pending', 'Done', 'With Results'],
};
const STATUS_COLOR = { 'Pending': '#FF9500', 'Done': '#30D158', 'With Results': '#BF5AF2' }; // eslint-disable-line no-unused-vars

export default function TasksTab({ todos, addTodo, togTodo, editTodo, delTodo, moveTodo, dropTodo, dragIdx, setDragIdx, dragOver, setDragOver, onAddLabResult = () => {}, onAddImagingResult = () => {} }) {
  const [addType, setAddType] = useState('general');
  const [addPriority, setAddPriority] = useState('routine');
  const [resultPrompt, setResultPrompt] = useState(null);

  const handleStatusChange = (t, newStatus) => {
    togTodo(t.id, { type: t.type || 'general', status: newStatus });
    if (newStatus === 'With Results') setResultPrompt({ todoId: t.id, type: t.type });
  };

  // Sort todos: urgent first, then soon, then routine — within each by order
  const PORD = { urgent: 0, soon: 1, routine: 2 };
  const sorted = [...todos].sort((a, b) => {
    const pa = PORD[a.priority || 'routine'], pb = PORD[b.priority || 'routine'];
    if (pa !== pb) return pa - pb;
    return (a.order ?? 0) - (b.order ?? 0);
  });

  return (
    <>
      {resultPrompt && (
        <div style={{ background: '#1A0A2A', border: '1px solid #BF5AF244', borderRadius: 12, padding: 14, marginBottom: 12 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: '#BF5AF2', marginBottom: 8 }}>Results in — add to {resultPrompt.type === 'labs' ? 'Labs' : 'Imaging'} tab?</div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={() => { resultPrompt.type === 'labs' ? onAddLabResult() : onAddImagingResult(); setResultPrompt(null); }}
              style={{ flex: 1, background: '#BF5AF222', border: '1px solid #BF5AF244', borderRadius: 8, color: '#BF5AF2', fontSize: 12, fontWeight: 700, padding: '8px 0', cursor: 'pointer' }}>
              + Add Results Now
            </button>
            <button onClick={() => setResultPrompt(null)} style={{ background: 'none', border: '1px solid ' + C.bd, borderRadius: 8, color: C.dim, fontSize: 12, padding: '8px 12px', cursor: 'pointer' }}>Later</button>
          </div>
        </div>
      )}

      <Card>
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}>
          <SLabel mb={0}>Tasks</SLabel>
          <span style={{ marginLeft: 6, fontSize: 9, color: C.dim2 }}>drag to reorder</span>
        </div>
        {/* Add controls */}
        <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap', marginBottom: 8, alignItems: 'center' }}>
          {/* Priority */}
          {TASK_PRIORITY.map(p => (
            <button key={p.k} onClick={() => setAddPriority(p.k)}
              style={{ padding: '3px 8px', borderRadius: 6, border: '1px solid ' + (addPriority === p.k ? p.color : C.bd), background: addPriority === p.k ? p.bg : 'transparent', color: addPriority === p.k ? p.color : C.dim, fontSize: 10, fontWeight: addPriority === p.k ? 700 : 400, cursor: 'pointer' }}>
              {p.label}
            </button>
          ))}
          <div style={{ width: 1, height: 16, background: C.bd, margin: '0 2px' }} />
          {/* Type */}
          {TASK_TYPES.map(tp => (
            <button key={tp.k} onClick={() => setAddType(tp.k)}
              style={{ padding: '3px 8px', borderRadius: 6, border: '1px solid ' + (addType === tp.k ? tp.color : C.bd), background: addType === tp.k ? tp.color + '22' : 'transparent', color: addType === tp.k ? tp.color : C.dim, fontSize: 10, fontWeight: addType === tp.k ? 700 : 400, cursor: 'pointer' }}>
              {tp.label}
            </button>
          ))}
          <button onClick={() => addTodo(addType, addPriority)}
            style={{ background: '#1A3D6A', color: '#7DC4FF', border: 'none', borderRadius: 6, padding: '4px 12px', fontSize: 12, cursor: 'pointer', marginLeft: 'auto' }}>
            + Add
          </button>
        </div>

        {todos.length === 0 && <div style={{ color: C.dim2, fontSize: 13, padding: '8px 0', textAlign: 'center' }}>No tasks</div>}

        {sorted.map((t, idx) => {
          const type = t.type || 'general';
          const tc = TASK_TYPES.find(tp => tp.k === type)?.color || '#C8DFF0';
          const status = t.status || (t.done ? 'Done' : 'Pending');
          const statuses = TASK_STATUS[type] || TASK_STATUS.general;
          const priority = t.priority || 'routine';
          const pr = TASK_PRIORITY.find(p => p.k === priority);
          const origIdx = todos.indexOf(t);
          const nextPriority = () => {
            const idx2 = TASK_PRIORITY.findIndex(p => p.k === priority);
            return TASK_PRIORITY[(idx2 + 1) % TASK_PRIORITY.length].k;
          };
          const nextStatus = () => {
            const idx2 = statuses.indexOf(status);
            return statuses[(idx2 + 1) % statuses.length];
          };
          const statusColors = { 'Pending': '#FF9500', 'Done': '#30D158', 'With Results': '#BF5AF2' };
          const sc = statusColors[status] || C.dim;

          return (
            <div key={t.id} draggable
              onDragStart={e => { setDragIdx(origIdx); e.dataTransfer.effectAllowed = 'move'; }}
              onDragOver={e => { e.preventDefault(); setDragOver(origIdx); }}
              onDrop={e => { e.preventDefault(); dropTodo(origIdx, todos); }}
              onDragEnd={() => { setDragIdx(null); setDragOver(null); }}
              style={{ padding: '8px 0', borderBottom: '1px solid ' + C.bg0, background: dragOver === origIdx ? '#1A3D6A22' : undefined, opacity: dragIdx === origIdx ? 0.5 : 1 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                {/* Drag handle */}
                <div style={{ color: C.dim2, fontSize: 13, cursor: 'grab', flexShrink: 0, userSelect: 'none', lineHeight: 1 }}>&#9776;</div>
                {/* Priority dot — click to cycle */}
                <span onClick={() => togTodo(t.id, { priority: nextPriority() })} title={'Priority: ' + priority + ' (click to change)'}
                  style={{ width: 8, height: 8, borderRadius: '50%', background: pr ? pr.color : C.dim2, flexShrink: 0, cursor: 'pointer', display: 'inline-block', border: '1px solid ' + (pr ? pr.color : C.dim2) + '44' }} />
                {/* Type badge (only if not general) */}
                {type !== 'general' && (
                  <span style={{ fontSize: 9, fontWeight: 800, color: tc, background: tc + '22', border: '1px solid ' + tc + '44', borderRadius: 4, padding: '1px 5px', flexShrink: 0 }}>{type.toUpperCase()}</span>
                )}
                {/* Task text */}
                <input value={t.text} onChange={e => editTodo(t.id, e.target.value)} placeholder="Task description..."
                  style={{ flex: 1, background: 'transparent', border: 'none', color: status === 'Done' ? '#2A5A3A' : C.tx, fontSize: 13, textDecoration: status === 'Done' ? 'line-through' : 'none', outline: 'none', minWidth: 0 }} />
                {/* Status pill — click to advance */}
                <button onClick={() => handleStatusChange(t, nextStatus())} title={'Status: ' + status + ' (click to advance)'}
                  style={{ padding: '3px 9px', borderRadius: 20, border: '1px solid ' + sc + '66', background: sc + '18', color: sc, fontSize: 10, fontWeight: 700, cursor: 'pointer', flexShrink: 0, whiteSpace: 'nowrap' }}>
                  {status}
                </button>
                {/* Delete */}
                <button onClick={() => delTodo(t.id)}
                  style={{ background: 'none', border: 'none', color: '#FF454566', cursor: 'pointer', fontSize: 14, padding: '2px 5px', flexShrink: 0, lineHeight: 1 }}>&times;</button>
              </div>
              {/* Timestamp */}
              {t.addedAt && (
                <div style={{ fontSize: 9, color: C.dim2, marginTop: 3, marginLeft: 28 }}>
                  {new Date(t.addedAt).toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' })}
                </div>
              )}
            </div>
          );
        })}
      </Card>
    </>
  );
}
